import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { supportedLanguages } from '../locales';
import { consolidateCanonicalPath } from '@shared/canonicalConsolidation';

const BASE_URL = 'https://solmi.ai';

interface SEOConfig {
  title: string;
  description: string;
  keywords?: string | string[];
  canonical?: string;
  noIndex?: boolean;
  structuredData?: object | object[];
}

/**
 * SEO hook that handles:
 * - Document title
 * - Meta description
 * - Open Graph tags
 * - Twitter Card tags
 * - Canonical URL
 * - Hreflang tags for all supported languages
 * - Structured data (JSON-LD)
 */
export function useSEO(config: SEOConfig) {
  const [location] = useLocation();
  const { title, description, keywords, canonical, noIndex, structuredData } = config;

  useEffect(() => {
    // Skip SEO updates if title is empty (parent component handles SEO)
    if (!title) return;

    // Update document title
    document.title = title;

    // Update meta description
    updateMetaTag('description', description);

    // Update meta keywords
    const keywordsContent = Array.isArray(keywords)
      ? keywords.join(', ')
      : keywords?.trim();
    if (keywordsContent) {
      updateMetaTag('keywords', keywordsContent);
    } else {
      removeMetaTag('keywords');
    }

    // Update Open Graph tags
    updateMetaProperty('og:title', title);
    updateMetaProperty('og:description', description);
    updateMetaProperty('og:url', canonical || getCanonicalUrl(location));

    // Update Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Update robots meta if noIndex is set
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      removeMetaTag('robots');
    }

    // Update canonical link
    updateCanonicalLink(canonical || getCanonicalUrl(location));

    // Update hreflang links
    updateHreflangLinks(location);

    // Update structured data
    if (structuredData) {
      updateStructuredData(structuredData);
    }

    // Cleanup on unmount
    return () => {
      removeHreflangLinks();
      removeStructuredData('seo-structured-data');
    };
  }, [title, description, keywords, canonical, noIndex, location, structuredData]);
}

/**
 * Get the canonical URL for the current path
 * Removes language prefix and uses the base English path as canonical
 */
function getCanonicalUrl(path: string): string {
  const pathParts = path.split('/').filter(Boolean);
  const potentialLang = pathParts[0];

  // If path starts with a language code, remove it for canonical
  if (supportedLanguages.includes(potentialLang as any)) {
    const pathWithoutLang = '/' + pathParts.slice(1).join('/');
    return `${BASE_URL}${normalizeSeoPath(pathWithoutLang || '/')}`;
  }

  return `${BASE_URL}${normalizeSeoPath(path)}`;
}

/**
 * Get the path without language prefix
 */
function getPathWithoutLang(path: string): string {
  const pathParts = path.split('/').filter(Boolean);
  const potentialLang = pathParts[0];

  if (supportedLanguages.includes(potentialLang as any)) {
    return normalizeSeoPath('/' + pathParts.slice(1).join('/'));
  }

  return normalizeSeoPath(path);
}

function normalizeSeoPath(path: string): string {
  const withoutQuery = path.split("?")[0]?.split("#")[0] || "/";
  if (!withoutQuery || withoutQuery === "/") return "/";

  const normalized = withoutQuery.endsWith("/") ? withoutQuery.slice(0, -1) : withoutQuery;

  // Keep blog listing URLs on the trailing-slash canonical to avoid /blog -> /blog/ redirect leakage.
  if (normalized === "/blog") return "/blog/";

  // Collapse thin /tools/* duplicates onto their rich top-level twin so the
  // JS-rendered canonical matches the server-rendered one (see
  // shared/canonicalConsolidation.ts).
  return consolidateCanonicalPath(normalized);
}

function updateMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeMetaTag(name: string) {
  const meta = document.querySelector(`meta[name="${name}"]`);
  if (meta) {
    meta.remove();
  }
}

function updateMetaProperty(property: string, content: string) {
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function updateCanonicalLink(url: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

function updateHreflangLinks(currentPath: string) {
  // Remove existing hreflang links
  removeHreflangLinks();

  const basePath = getPathWithoutLang(currentPath);

  // Add hreflang for each supported language
  supportedLanguages.forEach((lang) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang);
    link.setAttribute('data-hreflang', 'true'); // Mark for easy removal

    // English uses base path, other languages use /{lang}/path
    if (lang === 'en') {
      link.setAttribute('href', `${BASE_URL}${basePath || '/'}`);
    } else {
      link.setAttribute('href', `${BASE_URL}/${lang}${basePath}`);
    }

    document.head.appendChild(link);
  });

  // Add x-default hreflang (points to English version)
  const xDefaultLink = document.createElement('link');
  xDefaultLink.setAttribute('rel', 'alternate');
  xDefaultLink.setAttribute('hreflang', 'x-default');
  xDefaultLink.setAttribute('data-hreflang', 'true');
  xDefaultLink.setAttribute('href', `${BASE_URL}${basePath || '/'}`);
  document.head.appendChild(xDefaultLink);
}

function removeHreflangLinks() {
  const existingLinks = document.querySelectorAll(
    'link[data-hreflang="true"], link[rel="alternate"][hreflang]'
  );
  existingLinks.forEach((link) => link.remove());
}

function updateStructuredData(data: object | object[]) {
  removeStructuredData('seo-structured-data');

  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('id', 'seo-structured-data');
  script.textContent = JSON.stringify(Array.isArray(data) ? data : [data]);
  document.head.appendChild(script);
}

function removeStructuredData(id: string) {
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }
}

export default useSEO;
